import { CHART_SNAPSHOT, chartSnapshotCountries, getChartSnapshot } from './chartSnapshot.js';

export const CHART_HISTORY_MANIFEST_PATH = '/chart-history/v1/latest.json';

let cachedSourcePromise = null;

export function clearChartHistoryCache() {
  cachedSourcePromise = null;
}

export function loadChartHistorySource(fetchImpl = globalThis.fetch) {
  if (!cachedSourcePromise) {
    cachedSourcePromise = loadDailySource(fetchImpl).catch(() => fallbackSource());
  }
  return cachedSourcePromise;
}

async function loadDailySource(fetchImpl) {
  if (typeof fetchImpl !== 'function') throw new Error('fetch is not available');
  const manifest = await fetchJson(CHART_HISTORY_MANIFEST_PATH, fetchImpl);
  validateManifest(manifest);

  const snapshotByKey = new Map(
    manifest.snapshots.map((item) => [`${item.storefront}|${item.chartType}`, item]),
  );

  return {
    kind: 'daily',
    capturedAt: manifest.capturedAt,
    countries: [...manifest.storefronts],
    chartTypes: [...manifest.chartTypes],
    async getChart(country, chartType) {
      const item = snapshotByKey.get(`${String(country).toLowerCase()}|${chartType}`);
      if (!item) return [];
      const snapshot = await fetchJson(item.path, fetchImpl);
      validateSnapshot(snapshot, item, manifest);
      return snapshot.entries.map((entry) => ({
        rank: entry.rank,
        trackId: entry.trackId,
        name: entry.name,
        artistName: '',
        artworkUrl: entry.artworkUrl,
        primaryGenre: entry.primaryGenre,
        genreId: entry.genreId,
      }));
    },
  };
}

function fallbackSource() {
  return {
    kind: 'fallback',
    capturedAt: CHART_SNAPSHOT.capturedAt,
    countries: chartSnapshotCountries(),
    chartTypes: ['top-free', 'top-paid', 'top-grossing'],
    getChart(country, chartType) {
      return getChartSnapshot(country, chartType);
    },
  };
}

async function fetchJson(path, fetchImpl) {
  const res = await fetchImpl(path);
  if (!res?.ok) throw new Error(`Chart history fetch failed: ${path}`);
  return res.json();
}

function validateManifest(manifest) {
  if (!manifest || typeof manifest !== 'object') throw new Error('Invalid chart history manifest');
  if (manifest.schemaVersion !== 1) throw new Error('Unsupported chart history schema');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(manifest.capturedAt)) throw new Error('Invalid chart history date');
  if (!Array.isArray(manifest.storefronts) || manifest.storefronts.length === 0) {
    throw new Error('Chart history manifest has no storefronts');
  }
  if (!Array.isArray(manifest.chartTypes) || manifest.chartTypes.length === 0) {
    throw new Error('Chart history manifest has no chart types');
  }
  if (!Array.isArray(manifest.snapshots)) throw new Error('Chart history manifest has no snapshots');
}

function validateSnapshot(snapshot, item, manifest) {
  if (!snapshot || typeof snapshot !== 'object') throw new Error('Invalid chart history snapshot');
  if (snapshot.schemaVersion !== manifest.schemaVersion) throw new Error('Snapshot schema mismatch');
  if (snapshot.capturedAt !== manifest.capturedAt) throw new Error('Snapshot date mismatch');
  if (snapshot.storefront !== item.storefront) throw new Error('Snapshot storefront mismatch');
  if (snapshot.chartType !== item.chartType) throw new Error('Snapshot chart type mismatch');
  if (!Array.isArray(snapshot.entries)) throw new Error('Snapshot entries missing');
}
